/**
 * SplitScreenModal — side-by-side view of email content (left) and order detail (right).
 *
 * Can be opened from either direction:
 * - From email view: provides emailId + email data, resolves orderId automatically
 * - From order view: provides orderId + emailId, fetches email data automatically
 */

import { useState, useEffect, lazy, Suspense } from 'react';
import { portalService } from '../../services/portal';
import DetailNavButtons from './DetailNavButtons';
import type { DetailNavigation } from './detailNavigation';
import { useDetailNavKeys } from '../../hooks/useDetailNavKeys';
import { mapEmailToComponent } from '../../utils/mapEmailToComponent';
import type { ToastType } from '../../hooks/useToast';
import styles from './SplitScreenModal.module.css';

// Lazy imports to break circular dependency:
// EmailViewModal → SplitScreenModal → EmailViewModal
// OrderDetailModal → SplitScreenModal → OrderDetailModal
const EmailViewModal = lazy(() => import('./EmailViewModal'));
const OrderDetailModal = lazy(() => import('./OrderDetailModal'));

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface SplitScreenModalProps {
  /** Email data (pre-loaded, e.g. from EmailViewModal context). If null, fetched from emailId. */
  email?: any | null;
  /** Email ID — required to show email pane and resolve order */
  emailId: string;
  /** Order ID — if provided, skips order resolution from emailId */
  orderId?: string | null;
  onClose: () => void;
  onOrderAction?: (action: string, orderId: string) => void;
  onReplyClick?: (action: 'reply' | 'reply_all' | 'forward') => void;
  /** Explicit read/unread controls for the embedded email pane (never on open). */
  onMarkRead?: (emailId: string) => void;
  onMarkUnread?: (emailId: string) => void;
  isUnread?: boolean;
  onEmailChanged?: () => void;
  /** Surface success/error feedback in the parent's toast container. */
  onToast?: (message: string, type: ToastType) => void;
  /**
   * Prev / Next over the list the email was opened from (kanban 97abd058).
   * Given when this split view IS the email view (EmailViewModal opened a
   * linked email straight into it); the buttons sit in this header and
   * j / k / arrows step the list.
   */
  navigation?: DetailNavigation;
  /**
   * What closing records: 'email_close' when the split view is the email
   * view itself, 'split_view_close' (default) when it was opened over an
   * email or an order and closing returns there.
   */
  closeAction?: 'email_close' | 'split_view_close';
  /**
   * The order pane resolved its order from the email (null when the email
   * has none yet). Only fires when no orderId was given; EmailViewModal
   * records the automatic email_open_order row from it with both ids.
   */
  onOrderResolved?: (orderId: string | null) => void;
}

const SplitScreenModal: React.FC<SplitScreenModalProps> = ({
  email: propEmail,
  emailId,
  orderId,
  onClose,
  onOrderAction,
  onReplyClick,
  onMarkRead,
  onMarkUnread,
  isUnread,
  onEmailChanged,
  onToast,
  navigation,
  closeAction = 'split_view_close',
  onOrderResolved,
}) => {
  // If email data isn't provided, fetch it
  const [fetchedEmail, setFetchedEmail] = useState<any>(null);
  const [emailLoading, setEmailLoading] = useState(!propEmail);
  const [emailError, setEmailError] = useState<string | null>(null);

  // The order shown in the right pane. Starts as the prop, but switches when
  // a new draft is created from the email pane ("Add Another Order") so the
  // order pane shows the new draft and refreshes its sibling list.
  // Synced from the prop via render-time derived state (not an effect).
  const [activeOrderId, setActiveOrderId] = useState<string | null>(orderId ?? null);
  const [prevOrderIdProp, setPrevOrderIdProp] = useState<string | null>(orderId ?? null);
  if ((orderId ?? null) !== prevOrderIdProp) {
    setPrevOrderIdProp(orderId ?? null);
    setActiveOrderId(orderId ?? null);
  }

  const email = propEmail || fetchedEmail;

  // Fetch email data when not provided (opened from order direction)
  useEffect(() => {
    if (propEmail || !emailId) return;
    setEmailLoading(true);
    portalService
      .getEmailDetail(emailId)
      .then((data) => {
        setFetchedEmail(data ? mapEmailToComponent(data) : null);
      })
      .catch((err) => {
        console.error('SplitScreenModal: failed to load email', err);
        setEmailError('Failed to load email');
      })
      .finally(() => setEmailLoading(false));
  }, [emailId, propEmail]);

  // Close on Escape, unless a pane consumed it (the order's ship-to or
  // customer picker, the reply composer mark the key spent with
  // preventDefault). Decided in a macrotask, after EVERY listener has run: a
  // microtask is too early for a real key press, since the browser runs a
  // microtask checkpoint after each listener and this document listener
  // fires before the order's window listener.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        window.setTimeout(() => { if (!e.defaultPrevented) onClose(); }, 0);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Prev / Next keys. The embedded panes bind nothing themselves (modal mode
  // only), so this is the one binding while the split view is up.
  useDetailNavKeys(navigation, !!navigation);

  // No click-off close (kanban 2c097936): the split view holds an email and
  // an order the CSR may be mid-edit in; only the header close and Escape
  // dismiss it.
  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Compact header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="18" rx="1" />
              <rect x="14" y="3" width="7" height="18" rx="1" />
            </svg>
            Split View
            <span className={`${styles.headerBadge} ${styles.headerBadgeEmail}`}>Email</span>
            <span className={`${styles.headerBadge} ${styles.headerBadgeOrder}`}>Order</span>
          </div>
          {navigation && (
            <DetailNavButtons navigation={navigation} actionPrefix="email" noun="email" />
          )}
          <button
            className={styles.closeBtn}
            onClick={onClose}
            type="button"
            aria-label="Close split view"
            data-action={closeAction}
            data-outcome="close"
            data-target-email-id={emailId}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Side-by-side panes */}
        <div className={styles.panes}>
          {/* Left: Email. No per-pane label strip — the embedded email's own
              header (subject + sender) identifies the pane; a third chrome
              layer read as window-in-window (review 4ba0837c). */}
          <div className={styles.emailPane}>
            {emailLoading ? (
              <div className={styles.loadingState}>Loading email...</div>
            ) : emailError ? (
              <div className={styles.errorState}>{emailError}</div>
            ) : email ? (
              <Suspense fallback={<div className={styles.loadingState}>Loading...</div>}>
                <EmailViewModal
                  email={email}
                  emailId={emailId}
                  onClose={onClose}
                  onOrderAction={onOrderAction}
                  onReplyClick={onReplyClick}
                onMarkRead={onMarkRead}
                onMarkUnread={onMarkUnread}
                isUnread={isUnread}
                  onEmailChanged={onEmailChanged}
                  onOrderCreated={(_eid, oid) => {
                    setActiveOrderId(oid);
                    // Let the page behind the modal refresh its orders list/stats.
                    onOrderAction?.('created', oid);
                  }}
                  onToast={onToast}
                  renderMode="embedded"
                />
              </Suspense>
            ) : (
              <div className={styles.errorState}>Email not found</div>
            )}
          </div>

          {/* Right: Order — same rule, the order's own header (customer +
              status) is the pane's identity. */}
          <div className={styles.orderPane}>
            <Suspense fallback={<div className={styles.loadingState}>Loading...</div>}>
              <OrderDetailModal
                isOpen={true}
                onClose={onClose}
                emailId={emailId}
                orderId={activeOrderId}
                onOrderAction={onOrderAction}
                onOrderResolved={onOrderResolved}
                renderMode="embedded"
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitScreenModal;
